import { useCallback, useState } from 'react';

export interface UseCopyToClipboardResult {
  /** Attempt to copy `text` to the clipboard. Resolves with `true` on
   *  success and `false` on failure. Never throws — the caller converts
   *  the boolean into a toast + translation key. */
  copy: (text: string) => Promise<boolean>;
  /** True while a copy is in flight. Useful for disabling the trigger
   *  button during the microtask round-trip. */
  isCopying: boolean;
}

/**
 * Tiny clipboard helper. Tries the modern async clipboard API first;
 * falls back to the legacy `document.execCommand('copy')` path on a
 * temporary `<textarea>` when `navigator.clipboard` is unavailable
 * (older browsers, insecure contexts, some embedded WebViews).
 *
 * Never throws. Resolves with `true` on success and `false` on failure
 * so the dialog can render `admin.publishStore.errors.copyFailed`
 * without wrapping in try/catch.
 *
 * This hook has no external dependencies (Supabase, react-query,
 * translation) — it is browser-only so the test-engineer can mock
 * `navigator.clipboard` and `document.execCommand` to cover all
 * branches deterministically.
 */
export function useCopyToClipboard(): UseCopyToClipboardResult {
  const [isCopying, setIsCopying] = useState(false);

  const copy = useCallback(async (text: string): Promise<boolean> => {
    setIsCopying(true);
    try {
      // Primary path: the async clipboard API. `navigator.clipboard` is
      // undefined in insecure contexts and on older browsers, so we
      // feature-detect both the object and the method.
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch {
          // Fall through to the legacy path below. A permission error
          // on the async API commonly yields `DOMException: NotAllowed`
          // in iframes or when the document has lost focus — the
          // legacy path sometimes still succeeds.
        }
      }

      // Legacy fallback: create a hidden textarea, select it, and ask
      // the browser to copy its contents. `execCommand('copy')` is
      // deprecated but remains widely implemented and is our only hope
      // in contexts where the async API is blocked.
      if (typeof document === 'undefined') {
        return false;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      // Position off-screen but NOT `display: none` — hidden elements
      // cannot be selected. The `readOnly` attribute silences mobile
      // keyboards from popping up briefly.
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      try {
        textarea.select();
        textarea.setSelectionRange(0, text.length);
        const ok = document.execCommand('copy');
        return ok === true;
      } catch {
        return false;
      } finally {
        document.body.removeChild(textarea);
      }
    } finally {
      setIsCopying(false);
    }
  }, []);

  return { copy, isCopying };
}
